import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { defaultLocale } from '~/utils/language';
import { currencySymbol } from '~/utils/price';

type DualRangeProps = {
  name: string;
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (_value: [number, number]) => void;
  currency?: string;
  divisor?: number;
};

const DualRange = ({ name, min, max, step = 1, value, onChange, currency, divisor = 1 }: DualRangeProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language ?? defaultLocale;

  // Whole units only — shoppers filter by crowns, not hellers, and the slider steps
  // in whole units anyway, so a fractional part is only ever noise.
  const formatDisplay = useCallback(
    (v: number) => (v / divisor).toLocaleString(locale, { maximumFractionDigits: 0 }),
    [divisor, locale]
  );

  const symbol = useMemo(() => (currency ? currencySymbol(currency, locale) : undefined), [currency, locale]);

  const [local, setLocal] = useState<[number, number]>(value);
  const [minInput, setMinInput] = useState<string>(formatDisplay(value[0]));
  const [maxInput, setMaxInput] = useState<string>(formatDisplay(value[1]));
  const parseInput = (s: string) => {
    const cleaned = s.replace(/\s/g, '').replace(',', '.');
    return Number(cleaned) * divisor;
  };

  useEffect(() => {
    setLocal(value);
    setMinInput(formatDisplay(value[0]));
    setMaxInput(formatDisplay(value[1]));
  }, [value, divisor, locale, formatDisplay]);

  const [minVal, maxVal] = local;

  const clampMin = (v: number, maxV: number) => Math.min(Math.max(v, min), maxV - step);

  const clampMax = (v: number, minV: number) => Math.max(Math.min(v, max), minV + step);

  const commit = (source: 'min' | 'max') => {
    let nextMin = minInput === '' ? min : parseInput(minInput);
    let nextMax = maxInput === '' ? max : parseInput(maxInput);

    if (Number.isNaN(nextMin)) nextMin = min;
    if (Number.isNaN(nextMax)) nextMax = max;

    if (source === 'min') {
      nextMin = Math.min(Math.max(nextMin, min), nextMax - step);
    }

    if (source === 'max') {
      nextMax = Math.max(Math.min(nextMax, max), nextMin + step);
    }

    const next: [number, number] = [nextMin, nextMax];

    setLocal(next);
    setMinInput(formatDisplay(nextMin));
    setMaxInput(formatDisplay(nextMax));

    onChange(next);
  };

  const minPercent = ((minVal - min) / (max - min)) * 100;
  const maxPercent = ((maxVal - min) / (max - min)) * 100;

  return (
    <div className="dual-range">
      <div className="dual-range__slider">
        <div className="dual-range__track" />
        <div
          className="dual-range__range"
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`,
          }}
        />
        <Form.Label className="visually-hidden" htmlFor={`${name}-minRange`}>
          {t('components.filter.components.dual_range.range')}
        </Form.Label>
        <Form.Range
          id={`${name}-minRange`}
          min={min}
          max={max}
          step={step}
          value={minVal}
          onChange={(e) => {
            const v = clampMin(+e.target.value, maxVal);
            setLocal([v, maxVal]);
            setMinInput(formatDisplay(v));
          }}
          onMouseUp={() => commit('min')}
          onTouchEnd={() => commit('min')}
          onKeyUp={() => commit('min')}
          className="dual-range__thumb"
        />
        <Form.Label className="visually-hidden" htmlFor={`${name}-maxRange`}>
          {t('components.filter.components.dual_range.range')}
        </Form.Label>
        <Form.Range
          id={`${name}-maxRange`}
          min={min}
          max={max}
          step={step}
          value={maxVal}
          onChange={(e) => {
            const v = clampMax(+e.target.value, minVal);
            setLocal([minVal, v]);
            setMaxInput(formatDisplay(v));
          }}
          onMouseUp={() => commit('max')}
          onTouchEnd={() => commit('max')}
          onKeyUp={() => commit('max')}
          className="dual-range__thumb"
        />
      </div>
      <div className="dual-range__inputs">
        <Form.Group controlId={`${name}-minInput`} className="dual-range__input-group">
          <Form.Label className="visually-hidden">{t('components.filter.components.dual_range.min')} price</Form.Label>
          {symbol && <span className="dual-range__currency">{symbol}</span>}
          <Form.Control
            type="text"
            inputMode="decimal"
            value={minInput}
            placeholder={formatDisplay(min)}
            onChange={(e) => setMinInput(e.target.value)}
            onBlur={() => commit('min')}
            onKeyDown={(e) => e.key === 'Enter' && commit('min')}
            className="dual-range__input"
          />
        </Form.Group>

        <Form.Group controlId={`${name}-maxInput`} className="dual-range__input-group">
          <Form.Label className="visually-hidden">{t('components.filter.components.dual_range.max')} price</Form.Label>
          {symbol && <span className="dual-range__currency">{symbol}</span>}
          <Form.Control
            type="text"
            inputMode="decimal"
            value={maxInput}
            placeholder={formatDisplay(max)}
            onChange={(e) => setMaxInput(e.target.value)}
            onBlur={() => commit('max')}
            onKeyDown={(e) => e.key === 'Enter' && commit('max')}
            className="dual-range__input"
          />
        </Form.Group>
      </div>
    </div>
  );
};

export default DualRange;
